import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PricingGrid } from "@/pages/ChoosePlan";
import { startCheckout } from "@/pages/Billing";
import { CreditCard, Loader2, LogOut, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const eur = (p) => Number(p).toFixed(2).replace(".", ",");

// Page de blocage : essai gratuit terminé (trial_expired), abonnement direct non finalisé (pending_subscription) ou abonnement résilié/expiré (restricted).
export default function BillingPending() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/subscription").then((r) => { if (r.data.access_allowed) navigate("/app/dashboard", { replace: true }); else setSub(r.data); }).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.post("/track/event", { name: "pricing_viewed", props: { where: "billing_pending" } }).catch(() => {});
  }, [navigate]);
  const choose = async (code) => {
    setBusy(code);
    try { await startCheckout(code); } catch (e) { toast.error(e.response?.data?.detail || "Paiement indisponible"); setBusy(""); }
  };
  const reason = sub?.access_reason;
  if (reason === "trial_expired") {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10" data-testid="billing-pending" data-reason="trial_expired">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 text-center">
            <Clock className="w-10 h-10 mx-auto text-blue-600" />
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-3" data-testid="trial-expired-title">Votre essai gratuit de 7 jours est terminé.</h1>
            <p className="text-sm text-slate-600 mt-2" data-testid="trial-expired-message">Vos données sont conservées. Choisissez une offre pour reprendre là où vous vous êtes arrêté.</p>
            <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Établissement, audits, historique et plan d'action retrouvés immédiatement après le paiement.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-3" data-testid="trial-expired-plans">
            {plans.map((p) => (
              <div key={p.code} className={`bg-white rounded-2xl border p-5 flex flex-col ${p.popular ? "border-blue-400" : "border-slate-200"}`}>
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{p.name}</div>
                <div className="font-heading text-2xl font-extrabold mt-1" data-testid={`expired-price-${p.code}`}>{eur(p.price)} € <span className="text-xs font-normal text-slate-400">HT/mois</span></div>
                <p className="text-xs text-slate-500 mt-1 flex-1">{p.tagline}</p>
                <Button onClick={() => choose(p.code)} disabled={!!busy} className={`mt-4 rounded-xl h-11 ${p.popular ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"}`} data-testid={`expired-choose-${p.code}`}>{busy === p.code ? <Loader2 className="w-4 h-4 animate-spin" /> : `Choisir ${p.name}`}</Button>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-400">Paiement sécurisé Stripe. Aucune carte n'a été enregistrée pendant l'essai : rien n'a été prélevé.</p>
          <div className="text-center"><button onClick={logout} className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="pending-logout"><LogOut className="w-3 h-3" /> Se déconnecter</button></div>
        </div>
      </div>
    );
  }
  const restricted = reason === "restricted";
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10" data-testid="billing-pending" data-reason={reason || ""}>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 text-center">
          <CreditCard className="w-10 h-10 mx-auto text-blue-600" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-3">{restricted ? "Votre abonnement est inactif" : "Votre abonnement n'est pas encore activé"}</h1>
          <p className="text-sm text-slate-600 mt-2">{restricted ? "Votre abonnement a été résilié ou n'a pas pu être prélevé. Vos données sont conservées : réactivez votre offre pour retrouver votre espace." : "Pour accéder à Respondia, finalisez votre abonnement : paiement sécurisé Stripe, accès immédiat à l'offre choisie, résiliable à tout moment."}</p>
          {sub?.selected_plan_info && <p className="text-sm mt-3">Offre choisie : <b data-testid="pending-selected-plan">{sub.selected_plan_info.name}</b> — {eur(sub.selected_plan_info.price)} € HT/mois</p>}
          <div className="flex flex-col sm:flex-row gap-2 justify-center mt-5">
            <Button onClick={() => choose(sub?.selected_plan || "essential")} disabled={!!busy || !sub} className="rounded-xl h-11 bg-blue-600 hover:bg-blue-700" data-testid="pending-resume-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : restricted ? "Réactiver mon abonnement" : "Finaliser mon abonnement"}</Button>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6"><p className="text-xs text-slate-500 mb-3">Ou choisir une autre offre :</p><PricingGrid compact current={sub?.selected_plan} busy={!!busy} onSelect={choose} /></div>
        <div className="text-center"><button onClick={logout} className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="pending-logout"><LogOut className="w-3 h-3" /> Se déconnecter</button></div>
      </div>
    </div>
  );
}
